import api from './client'

// Reference sources for the code-adoption flow: an EXTERNAL application's
// source code registered on a project (read-only) so the Ask-AI agent can
// analyze it and replicate the app. Backed by /api/reference-sources.

export interface ReferenceSource {
  id: string
  kind: 'git' | 'local' | 'zip'
  display: string
  origin: Record<string, string>
  root: string
  ingested_at: string
  stats: {
    files: number
    bytes: number
    languages: Record<string, number>
    frameworks: string[]
  }
  status: 'ready' | 'missing'
}

export async function listReferenceSources(project: string): Promise<ReferenceSource[]> {
  const { data } = await api.get(`/reference-sources/${encodeURIComponent(project)}`)
  return data.sources || []
}

export async function registerGitSource(
  project: string, url: string, ref?: string,
): Promise<ReferenceSource> {
  const { data } = await api.post(
    `/reference-sources/${encodeURIComponent(project)}/git`,
    { url, ref: ref || '' },
    // A shallow clone of a big repo can take a while.
    { timeout: 660_000 },
  )
  return data.source
}

export async function registerLocalPathSource(
  project: string, path: string,
): Promise<ReferenceSource> {
  const { data } = await api.post(
    `/reference-sources/${encodeURIComponent(project)}/local-path`,
    { path },
    { timeout: 120_000 },
  )
  return data.source
}

export async function uploadZipSource(
  project: string, file: File, onProgress?: (pct: number) => void,
): Promise<ReferenceSource> {
  const form = new FormData()
  form.append('file', file)
  const { data } = await api.post(
    `/reference-sources/${encodeURIComponent(project)}/zip`,
    form,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 660_000,
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100))
      },
    },
  )
  return data.source
}

export async function deleteReferenceSource(project: string, sourceId: string) {
  const { data } = await api.delete(
    `/reference-sources/${encodeURIComponent(project)}/${encodeURIComponent(sourceId)}`)
  return data
}

// One-shot dock hand-off: the workbench "Replicate from Source Code" flow
// seeds the Ask-AI dock with the analysis prompt for the new project.
const DOCK_SEED_KEY = 'twasta_dock_seed_prompt'

export function seedDockPrompt(project: string, prompt: string) {
  try {
    localStorage.setItem(DOCK_SEED_KEY, JSON.stringify({ project, prompt }))
  } catch { /* storage full/unavailable — hand-off is best-effort */ }
}

export function takeDockSeedPrompt(project: string): string | null {
  try {
    const raw = localStorage.getItem(DOCK_SEED_KEY)
    if (!raw) return null
    const seed = JSON.parse(raw)
    if (!seed || seed.project !== project || !seed.prompt) return null
    localStorage.removeItem(DOCK_SEED_KEY)
    return String(seed.prompt)
  } catch {
    return null
  }
}
